import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Info } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useTranslation } from '../../context/LanguageContext';
import { getPageBySlug, getPageByModuleCode } from '../../services/pagesService';
import PageBlocks from './PageBlocks';

/**
 * ContentPage - Rendu d'une page éditoriale
 *
 * Sert deux routes :
 *   /p/:slug         → une page quelconque, résolue par son slug
 *   /module/:code    → la page décrivant un module, résolue par moduleCode
 *
 * Le contenu vient de la base sous forme de blocs typés, validés à l'écriture
 * (cabaza_pages.cfc, ValidateBodyJson) et rendus par des composants dédiés.
 * Une page absente dans la langue du visiteur lui est servie en français, et
 * le repli lui est signalé.
 *
 * @param {'slug'|'module'} resolveBy - Comment la page est désignée dans l'URL
 */
const ContentPage = ({ resolveBy = 'slug' }) => {
    const { t, language } = useTranslation();
    const params = useParams();
    const identifier = resolveBy === 'module' ? params.code : params.slug;

    const [state, setState] = useState({ loading: true, result: null });

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setState({ loading: true, result: null });

            const fetcher = resolveBy === 'module' ? getPageByModuleCode : getPageBySlug;
            const result = await fetcher(identifier, language);

            // La langue peut changer pendant la requête : on ignore alors une
            // réponse devenue obsolète plutôt que d'afficher la mauvaise page.
            if (!cancelled) {
                setState({ loading: false, result });
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [identifier, language, resolveBy]);

    const { loading, result } = state;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header />
                <main className="grow max-w-4xl mx-auto px-4 py-12 w-full">
                    <div className="flex items-center justify-center py-12" data-testid="page-loading">
                        <Loader2 className="w-8 h-8 animate-spin text-cabaza-purple" />
                        <span className="ml-3 text-gray-500">
                            {t.common?.loading || 'Chargement...'}
                        </span>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // Page absente, en brouillon, ou contenu illisible : une page 404 en bonne
    // et due forme, jamais un écran blanc.
    if (!result || !result.found || !result.page) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header />
                <main className="grow max-w-4xl mx-auto px-4 py-12 w-full">
                    <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 text-center" data-testid="page-not-found">
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">
                            {t.pages?.notFoundTitle || 'Page introuvable'}
                        </h1>
                        <p className="text-gray-600 mb-8">
                            {t.pages?.notFoundText || "Cette page n'existe pas ou n'est pas encore publiée."}
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center px-5 py-2.5 rounded-xl bg-gradient-to-br from-cabaza-purple to-cabaza-pink text-white font-medium hover:opacity-90 transition-opacity"
                        >
                            {t.pages?.backHome || "Retour à l'accueil"}
                        </Link>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    const { page, isFallback } = result;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            <main className="grow max-w-4xl mx-auto px-4 py-12 w-full">
                <article className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
                    {/* Le visiteur doit savoir qu'il lit une version dans une
                        autre langue que la sienne — discrètement, sans bloquer
                        la lecture. */}
                    {isFallback && (
                        <div
                            className="flex items-start gap-2 mb-8 px-4 py-3 rounded-xl bg-purple-50 border border-purple-100 text-sm text-purple-700"
                            data-testid="page-fallback-notice"
                        >
                            <Info className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>
                                {t.pages?.fallbackNotice || "Cette page n'est pas encore traduite dans votre langue : elle vous est présentée en français."}
                            </span>
                        </div>
                    )}

                    <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="page-title">
                        {page.title}
                    </h1>

                    {/*
                        Le corps est un tableau de blocs typés, chacun rendu par
                        son composant (voir PageBlocks). Aucun HTML de mise en
                        page ne transite par la base : seuls les champs de texte
                        courant contiennent du balisage, et ils ont traversé
                        getSafeHTML à l'écriture.
                    */}
                    <div data-testid="page-body">
                        <PageBlocks blocs={page.blocs} />
                    </div>
                </article>
            </main>
            <Footer />
        </div>
    );
};

export default ContentPage;
